/*
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 */
import { jsPDF } from 'jspdf';

import { Logger } from '@terrestris/base-util';

export const pdfPrintFunc = async (
  mapImgUrl: string,
  pdfSpec: any,
  title: string,
  legendTitle: string) => {

  // initializes the PDF document
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4', // 210 by 297mm
    putOnlyUsedFonts: true
  });

  const mapWidth = pdfSpec.size[0];
  const mapHeight = pdfSpec.size[1];

  // add the map
  doc.addImage(mapImgUrl, 'PNG', 10, 17, mapWidth, mapHeight);

  // add a title
  doc.setFont('arial', 'bold');
  doc.setFontSize(20);
  doc.text(title, doc.internal.pageSize.width / 2, 13, { align: 'center' });

  // add attribution
  doc.setFont('arial', 'normal');
  doc.setFontSize(9);
  // doc.text(getAttributionsText(pdfSpec), doc.internal.pageSize.width - 10, 195, { align: 'right' });

  if (pdfSpec.layers?.some((l: any) => l.legendUrl && l.legendUrl.length > 0)) {
    // add second page for legends
    doc.addPage('a4', 'l');

    // add a legend title
    doc.setFont('arial', 'bold');
    doc.setFontSize(20);
    doc.text(legendTitle, doc.internal.pageSize.width / 2, 13, { align: 'center' });

    let xPosition = 20;

    for (let i = 0; i < pdfSpec.layers?.length; i++) {
      const layer: any = pdfSpec.layers?.[i];
      const legendUrl = layer.legendUrl;
      const name = layer.layerName;

      if (legendUrl && name) {
        try {
          // determine legend image dimensions
          const response = await fetch(legendUrl);
          const blob = await response.blob();
          const base64 = URL.createObjectURL(blob);
          const img: HTMLImageElement = new Image();
          img.src = base64.toString();
          await img.decode();

          const fontSize = 12;
          const textTopMargin = 30;
          const legendTopMargin = textTopMargin + 4;
          const distanceToNextLegend = 40;

          doc.setFont('arial', 'bold');
          doc.setFontSize(fontSize);
          doc.text(name, xPosition, textTopMargin, { align: 'left' });
          const widthMm = img.width * 25.4 / pdfSpec.dpi;
          const heightMm = img.height * 25.4 / pdfSpec.dpi;
          doc.addImage(legendUrl, 'PNG', xPosition, legendTopMargin, widthMm, heightMm);
          xPosition += widthMm + distanceToNextLegend;
        } catch (error) {
          Logger.error(error);
        }
      }
    }
  }
  return doc;
};
